import { useLevels } from '@studio/design-system/audio/levels';
import type { UseLevelsArgs } from '@studio/design-system/audio/levels';
import { Meter as _Meter, plotDecibelValue } from '@studio/design-system/audio/meter';
import { ensureNotNil } from '@studio/js-utils';
import { amplitudeToDb } from '@studio/react-utils/audio';
import { χref } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Placeholder } from './placeholder.jsx';
import type { Props } from './types.js';
import { sound } from '#~/audio/sound/index.js';
import { useAudioTrack } from '#~/livekit/hooks.js';

const Inner = (
  props: Pick<Props, 'size'> & Pick<UseLevelsArgs, 'audio' | 'context'>
): ReactElement => {
  const levels = useLevels(props);

  return <_Meter value={plotDecibelValue(amplitudeToDb(levels.peak))} />;
};

/** `<Meter>` component. */
export const Meter = ({ deviceId, size, ...props }: Props): ReactElement => {
  const track = useAudioTrack(deviceId);
  const [audioNode, setAudioNode] = useState<AudioNode>();
  const audioRef = useRef<HTMLAudioElement>(null);

  const { context } = sound;

  useEffect(() => {
    if (!track) {
      setAudioNode(undefined);

      return noop;
    }

    // Create a media stream from the track.
    const stream = new MediaStream();

    stream.addTrack(track.mediaStreamTrack);

    // Workaround for Chrome silent audio issue
    const audioElement = ensureNotNil(χref(audioRef));

    audioElement.srcObject = stream;

    // Create a media stream source from the stream.
    const node = context.createMediaStreamSource(stream);

    setAudioNode(node);

    return () => {
      // Clean up the media stream.
      stream.removeTrack(track.mediaStreamTrack);
      node.disconnect();
    };
  }, [context, track]);

  return (
    <div {...props}>
      {audioNode ? (
        <Inner audio={audioNode} context={context} size={size} />
      ) : (
        <Placeholder size={size} />
      )}
      <audio className='hidden' ref={audioRef} />
    </div>
  );
};
